"use client";

import { useEffect, useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { FileText } from "lucide-react";
import type { Student } from "@/lib/student.schema";
import { useStudentPapers } from "@/hooks/usePaperCatalog";

interface DocumentsProps {
	student: Student;
	onDocumentsChange?: (documentIds: string[]) => void;
	/**
	 * Tài khoản chỉ-xem: khoá mọi checkbox bất kể hồ sơ đã duyệt hay chưa.
	 *
	 * Chỉ là lớp giao diện — server chặn thật ở `denyReadOnly`. Nhưng khoá ở đây
	 * còn có tác dụng thứ hai đáng kể: một ô tick được sẽ ghi bản nháp vào
	 * localStorage của máy đó, tạo ra thay đổi cục bộ mà người dùng không bao giờ
	 * lưu được lên server.
	 */
	readOnly?: boolean;
}

/**
 * Bumped to `-v3`. Two reasons, both about what a saved entry is allowed to mean.
 *
 * `-v1` keyed state by array index, so reordering a method's papers silently
 * re-mapped which document an index referred to.
 *
 * `-v2` stored the WHOLE computed state, defaults included, and wrote it on the
 * first render before the operator touched anything. While the catalog shipped
 * `defaultChecked = true` for every paper, merely opening a hồ sơ persisted an
 * all-ticked entry — and because a saved entry outranks the defaults, fixing the
 * catalog could not undo it. Those entries are still sitting in every cán bộ's
 * browser, where no deploy can reach them; only a new key retires them.
 *
 * Old entries are dropped, never migrated: a `-v2` entry cannot say whether a
 * ticked box was the operator's decision or just the old default, which is
 * exactly the information the new format exists to carry.
 */
function storageKey(mssv: string): string {
	return `student-${mssv}-checkboxes-v3`;
}

export default function DocumentList({
	student,
	onDocumentsChange,
	readOnly = false,
}: DocumentsProps) {
	// The backend already unions "currently required by this diện" with
	// "already recorded in GiayTo" and evaluates every visibility rule — this
	// component renders that result, it never decides visibility itself. See
	// `catalog.controller.ts#listStudentPapers` for why: a frontend-side
	// filter is exactly how the previous version of this component silently
	// dropped ids from `GiayTo` (a paper retired from the mapping had no key
	// in checkbox state, so the emit effect below wrote it out of existence).
	const { studentPapers, loading, error } = useStudentPapers(student.mssv);
	const requiredPapers = studentPapers.filter((sp) => !sp.recordedOnly);
	const recordedOnlyPapers = studentPapers.filter((sp) => sp.recordedOnly);

	// Keyed by paperId, not array index — the whole point of the -v2 storage
	// key is that a reorder of `studentPapers` never changes which entry a
	// saved checkbox state refers to.
	const [checkedDocuments, setCheckedDocuments] = useState<Record<number, boolean>>({});
	const [initialized, setInitialized] = useState(false);

	// Những paperId người dùng TỰ tay bật/tắt. Chỉ những id này được lưu xuống
	// localStorage.
	//
	// Đây là điểm khác cốt lõi so với `-v2`, vốn lưu cả state đã tính: một ô đúng
	// vì `defaultChecked` không phân biệt được với một ô người dùng chủ ý tick,
	// nên bản nháp vô tình đóng băng luôn default tại thời điểm mở hồ sơ và ghi đè
	// mọi thay đổi default về sau. Giữ riêng tập "đã chạm" thì bản nháp chỉ nói về
	// quyết định của con người, còn phần còn lại luôn tính lại từ dữ liệu server.
	const [touchedPaperIds, setTouchedPaperIds] = useState<Set<number>>(new Set());

	// Initialize checkbox state once the student's checklist has loaded: prefer
	// a valid saved localStorage entry, then ho_so_da_nhan, then each row's
	// defaultChecked.
	useEffect(() => {
		if (loading) return;

		// Refuse to initialize when there is no checklist to speak for: a failed
		// fetch, or a diện nobody has mapped yet AND nothing recorded for this
		// student either. Staying uninitialized keeps the emit effect below
		// silent, and silence is the point — emitting `[]` would make the parent
		// send `ho_so_da_nhan: []`, which `student-api.service.ts` writes as
		// `GiayTo = ""` and destroys the student's existing record. The saved
		// draft is left untouched too, so a transient 5xx costs nothing.
		if (error || studentPapers.length === 0) {
			setInitialized(false);
			return;
		}

		const receivedIds = new Set(student.ho_so_da_nhan ?? []);

		// Defaults for EVERY paper the checklist mentions, computed first so that
		// a paper attached after this student's entry was written still gets a
		// key. Without it the checkbox rendered from `defaultChecked` while the
		// emit loop — which walks this state — silently omitted it.
		const defaults: Record<number, boolean> = {};
		studentPapers.forEach((sp) => {
			defaults[sp.paperId] =
				receivedIds.size > 0 ? receivedIds.has(String(sp.paperId)) : sp.defaultChecked;
		});

		let nextState = defaults;
		let restoredTouched = new Set<number>();

		const savedRaw = localStorage.getItem(storageKey(student.mssv));
		if (savedRaw) {
			let restored: Record<number, boolean> | null = null;
			try {
				const parsed = JSON.parse(savedRaw) as { documents?: Record<string, boolean> };
				const savedDocuments = parsed.documents ?? {};
				const savedKeys = Object.keys(savedDocuments);
				// Every saved key must still belong to this checklist. Keys the entry
				// does not mention keep their default rather than disappearing.
				const isValid =
					savedKeys.length > 0 && savedKeys.every((key) => defaults[Number(key)] !== undefined);

				if (isValid) {
					restored = { ...defaults };
					for (const [key, value] of Object.entries(savedDocuments)) {
						restored[Number(key)] = value;
					}
					// Các khoá trong bản nháp CHÍNH LÀ tập "đã chạm": chúng phải giữ
					// nguyên trạng thái đó khi lưu lại, nếu không thao tác thứ hai của
					// người dùng sẽ xoá mất thao tác thứ nhất.
					restoredTouched = new Set(savedKeys.map(Number));
				}
			} catch {
				// Corrupt entry — fall through to defaults and drop it below.
			}

			if (restored) {
				nextState = restored;
			} else {
				localStorage.removeItem(storageKey(student.mssv));
			}
		}

		setCheckedDocuments(nextState);
		setTouchedPaperIds(restoredTouched);
		setInitialized(true);
	}, [student.mssv, student.ho_so_da_nhan, loading, error, studentPapers]);

	// Persist and notify the parent whenever checkbox state changes.
	useEffect(() => {
		if (!initialized) return;

		// Chỉ lưu những ô người dùng tự chạm. Chưa chạm gì thì KHÔNG ghi khoá nào —
		// một bản nháp rỗng không mang thông tin nào mà lại lấn quyền của default
		// khi danh mục hồ sơ đổi.
		const draft: Record<number, boolean> = {};
		for (const paperId of touchedPaperIds) {
			if (checkedDocuments[paperId] !== undefined) draft[paperId] = checkedDocuments[paperId];
		}

		if (Object.keys(draft).length > 0) {
			localStorage.setItem(storageKey(student.mssv), JSON.stringify({ documents: draft }));
		} else {
			localStorage.removeItem(storageKey(student.mssv));
		}

		if (onDocumentsChange) {
			// The wire contract is unchanged: student-api.service.ts joins this
			// into `GiayTo` as a comma-separated string, and student.schema.ts
			// splits it back. Paper ids from the API are numbers; this is the one
			// place that converts to the string shape the wire contract expects.
			// The backend also merges this against what is already on record
			// (`StudentService.updateStudentByMSSV`), so an id this component
			// never rendered a checkbox for still cannot be lost even if this
			// emit somehow omitted it.
			const documentIds = Object.entries(checkedDocuments)
				.filter(([, checked]) => checked)
				.map(([paperId]) => paperId);
			onDocumentsChange(documentIds);
		}
	}, [checkedDocuments, touchedPaperIds, initialized, student.mssv, onDocumentsChange]);

	const handleDocumentCheck = (paperId: number, checked: boolean) => {
		// Disable editing if already approved
		if (student.trang_thai_duyet === "approved") return;
		// Chặn cả ở đây, không chỉ bằng prop `disabled` của Checkbox: hàm này ghi bản
		// nháp vào localStorage, nên để nó chạy được là tạo ra thay đổi cục bộ mà
		// người dùng không bao giờ lưu lên server được.
		if (readOnly) return;

		// Đánh dấu "đã chạm" ngay cả khi giá trị trùng với default: người dùng bỏ
		// tick một ô mặc định bật, rồi tải lại trang, phải thấy nó vẫn tắt.
		setTouchedPaperIds((prev) => new Set(prev).add(paperId));
		setCheckedDocuments((prev) => ({
			...prev,
			[paperId]: checked,
		}));
	};

	const checkedRequiredCount = requiredPapers.filter((sp) => checkedDocuments[sp.paperId]).length;

	if (loading) {
		return (
			<div className="flex-1">
				<div className="flex items-center gap-2 mb-2 mt-2">
					<FileText className="h-5 w-5 text-blue-600" />
					<span className="font-semibold text-lg text-blue-800">Hồ sơ</span>
				</div>
				<div className="space-y-2">
					{Array.from({ length: 6 }).map((_, i) => (
						<div key={i} className="flex items-start gap-2">
							<Skeleton className="h-4 w-4 mt-0.5 rounded-sm flex-shrink-0" />
							<Skeleton className="h-3 flex-1" />
						</div>
					))}
				</div>
			</div>
		);
	}

	return (
		<div className="flex-1">
			<div className="flex items-center gap-2 mb-2 mt-2">
				<FileText className="h-5 w-5 text-blue-600" />
				<span className="font-semibold text-lg text-blue-800">
					Hồ sơ ({checkedRequiredCount} / {requiredPapers.length})
				</span>
			</div>
			{error ? (
				// Distinct from the empty state on purpose: "not configured" invites
				// the operator to approve anyway, and here nothing is known at all.
				<p className="text-xs text-red-600">
					Không tải được danh sách hồ sơ. Vui lòng tải lại trang trước khi duyệt.
				</p>
			) : studentPapers.length === 0 ? (
				<p className="text-xs text-muted-foreground">chưa cấu hình hồ sơ cho diện này</p>
			) : (
				<div className="space-y-1 overflow-y-auto">
					{requiredPapers.map((sp) => (
						<DocumentRow
							key={sp.paperId}
							paperId={sp.paperId}
							name={sp.paper.name}
							active={sp.paper.active}
							checked={checkedDocuments[sp.paperId] ?? sp.defaultChecked}
							disabled={readOnly || student.trang_thai_duyet === "approved"}
							onCheckedChange={handleDocumentCheck}
						/>
					))}
					{recordedOnlyPapers.length > 0 && (
						<div className="pt-1 mt-1 border-t border-dashed space-y-1">
							<p className="text-[11px] text-muted-foreground">
								Đã thu, không còn nằm trong danh mục yêu cầu:
							</p>
							{recordedOnlyPapers.map((sp) => (
								<DocumentRow
									key={sp.paperId}
									paperId={sp.paperId}
									name={sp.paper.name}
									active={sp.paper.active}
									checked={checkedDocuments[sp.paperId] ?? sp.defaultChecked}
									disabled={readOnly || student.trang_thai_duyet === "approved"}
									onCheckedChange={handleDocumentCheck}
									recordedOnly
								/>
							))}
						</div>
					)}
				</div>
			)}
		</div>
	);
}

interface DocumentRowProps {
	paperId: number;
	name: string;
	active: boolean;
	checked: boolean;
	disabled: boolean;
	recordedOnly?: boolean;
	onCheckedChange: (paperId: number, checked: boolean) => void;
}

function DocumentRow({
	paperId,
	name,
	active,
	checked,
	disabled,
	recordedOnly,
	onCheckedChange,
}: DocumentRowProps) {
	return (
		<div className="flex items-start space-x-2 text-sm">
			<Checkbox
				id={`doc-${paperId}`}
				checked={checked}
				onCheckedChange={(next: boolean) => onCheckedChange(paperId, next)}
				className="h-4 w-4 mt-0.5 flex-shrink-0"
				disabled={disabled}
			/>
			<label
				htmlFor={`doc-${paperId}`}
				className={`flex-1 leading-relaxed cursor-pointer text-xs ${
					disabled ? "text-muted-foreground" : ""
				} ${!active || recordedOnly ? "italic text-muted-foreground" : ""}`}
			>
				{name}
			</label>
		</div>
	);
}
